import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { contasPagarService } from "../services/contasPagarService";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/apiClient";
import type { ContaPagar, BaixarContaPagarRequest, BaixarLoteRequest, FiltroContasPagar, TotaisFinanceiros,
  AtualizarContaPagarRequest, BaixarParcelaPagarRequest, BaixarParcelasPagarLoteRequest,
  FiltroGerencialPagar } from "../types";

const TOTAIS_ZERADO: TotaisFinanceiros = {
  totalEmAberto: 0, totalQuitado: 0, totalAtrasado: 0, totalGeral: 0,
  qtdTotal: 0, qtdEmAberto: 0, qtdQuitado: 0, qtdAtrasado: 0,
  proximoVencimento: null, proximoVencimentoValor: null,
};

export const useContasPagar = (filtro: FiltroContasPagar = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading } = useQuery({
    queryKey: ["contas-pagar", filtro],
    queryFn: () => contasPagarService.getAll(filtro),
    placeholderData: (prev) => prev,
  });

  const createMutation = useMutation({
    mutationFn: contasPagarService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-pagar"] });
      toast.success("Conta a pagar criada com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ContaPagar> }) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      contasPagarService.update(id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-pagar"] });
      toast.success("Conta a pagar atualizada com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: contasPagarService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-pagar"] });
      toast.success("Conta a pagar excluída com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const baixarMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: BaixarContaPagarRequest }) =>
      contasPagarService.baixar(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-pagar"] });
      toast.success("Conta baixada com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const baixarLoteMutation = useMutation({
    mutationFn: (req: BaixarLoteRequest) => contasPagarService.baixarLote(req),
    onSuccess: (resultados) => {
      queryClient.invalidateQueries({ queryKey: ["contas-pagar"] });
      const pagas = resultados.filter((r) => r.isPago).length;
      toast.success(`${pagas} conta(s) baixada(s) com sucesso`);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const estornarMutation = useMutation({
    mutationFn: (id: number) => contasPagarService.estornar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-pagar"] });
      toast.success("Estorno realizado com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  return {
    contas: paginado?.itens ?? [],
    total: paginado?.total ?? 0,
    totalPaginas: paginado?.totalPaginas ?? 0,
    totais: paginado?.totais ?? TOTAIS_ZERADO,
    isLoading,
    createConta: createMutation.mutate,
    updateConta: updateMutation.mutate,
    deleteConta: deleteMutation.mutate,
    baixarConta: baixarMutation.mutate,
    isBaixando: baixarMutation.isPending,
    baixarLote: baixarLoteMutation.mutate,
    isBaixandoLote: baixarLoteMutation.isPending,
    estornarConta: estornarMutation.mutate,
    isEstornando: estornarMutation.isPending,
  };
};

/**
 * Tela operacional de Contas a Pagar: lista e opera POR PARCELA (espelho de
 * `useParcelasReceber`).
 *
 * O `useContasPagar` acima continua servindo a visão por TÍTULO (drawer de
 * detalhe e telas gerenciais). São granularidades diferentes de propósito —
 * misturá-las é o que produzia "quitei uma parcela e sumiu o título todo".
 *
 * Toda mutação invalida as DUAS chaves, senão a grid por parcela e os KPIs por
 * título se desencontram depois de uma baixa.
 */
export const useParcelasPagar = (filtro: FiltroContasPagar = {}) => {
  const queryClient = useQueryClient();

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["contas-pagar-parcelas"] });
    queryClient.invalidateQueries({ queryKey: ["contas-pagar"] });
  };

  const { data: paginado, isLoading } = useQuery({
    queryKey: ["contas-pagar-parcelas", filtro],
    queryFn: () => contasPagarService.getParcelas(filtro),
    placeholderData: (prev) => prev,
  });

  const criarMutation = useMutation({
    mutationFn: contasPagarService.create,
    onSuccess: (conta) => {
      invalidar();
      const qtd = conta?.baixas?.length ?? 0;
      toast.success(
        qtd > 1
          ? `Conta a pagar lançada em ${qtd} parcelas`
          : "Conta a pagar lançada com sucesso",
      );
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const atualizarMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarContaPagarRequest }) =>
      contasPagarService.update(id, data),
    onSuccess: () => {
      invalidar();
      toast.success("Conta a pagar atualizada");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const excluirTituloMutation = useMutation({
    mutationFn: (tituloId: number) => contasPagarService.delete(tituloId),
    onSuccess: () => {
      invalidar();
      toast.success("Conta a pagar excluída (título e parcelas)");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const excluirParcelaMutation = useMutation({
    mutationFn: (parcelaId: number) => contasPagarService.deleteParcela(parcelaId),
    onSuccess: () => {
      invalidar();
      toast.success("Parcela excluída");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const baixarParcelaMutation = useMutation({
    mutationFn: ({ parcelaId, req }: { parcelaId: number; req: BaixarParcelaPagarRequest }) =>
      contasPagarService.baixarParcela(parcelaId, req),
    onSuccess: (r) => {
      invalidar();
      toast.success(r.mensagem);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const baixarParcelasLoteMutation = useMutation({
    mutationFn: (req: BaixarParcelasPagarLoteRequest) => contasPagarService.baixarParcelasLote(req),
    onSuccess: (resultados) => {
      invalidar();
      const ok = resultados.filter((r) => r.parcelaQuitada).length;
      toast.success(`${ok} parcela(s) paga(s)`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const estornarParcelaMutation = useMutation({
    mutationFn: (parcelaId: number) => contasPagarService.estornarParcela(parcelaId),
    onSuccess: () => {
      invalidar();
      toast.success("Estorno da parcela realizado");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  /** Quitação do TÍTULO inteiro — operação distinta da baixa de parcela. */
  const quitarTituloMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: BaixarContaPagarRequest }) =>
      contasPagarService.baixar(id, req),
    onSuccess: () => {
      invalidar();
      toast.success("Título quitado por completo");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const estornarTituloMutation = useMutation({
    mutationFn: (id: number) => contasPagarService.estornar(id),
    onSuccess: () => {
      invalidar();
      toast.success("Estorno do título realizado");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    parcelas: paginado?.itens ?? [],
    total: paginado?.total ?? 0,
    totalPaginas: paginado?.totalPaginas ?? 0,
    totais: paginado?.totais ?? TOTAIS_ZERADO,
    isLoading,

    criarConta: criarMutation.mutate,
    isCriando: criarMutation.isPending,
    atualizarConta: atualizarMutation.mutate,
    isAtualizando: atualizarMutation.isPending,

    excluirTitulo: excluirTituloMutation.mutate,
    excluirParcela: excluirParcelaMutation.mutate,

    baixarParcela: baixarParcelaMutation.mutate,
    isBaixandoParcela: baixarParcelaMutation.isPending,
    baixarParcelasLote: baixarParcelasLoteMutation.mutate,
    isBaixandoLote: baixarParcelasLoteMutation.isPending,
    estornarParcela: estornarParcelaMutation.mutate,
    isEstornandoParcela: estornarParcelaMutation.isPending,

    quitarTitulo: quitarTituloMutation.mutate,
    isQuitandoTitulo: quitarTituloMutation.isPending,
    estornarTitulo: estornarTituloMutation.mutate,
    isEstornandoTitulo: estornarTituloMutation.isPending,
  };
};

// ── Visão gerencial ────────────────────────────────────────────────────────
// Consultas puras (sem mutação): a gerencial mostra COMO ESTÁ, e as operações
// continuam na tela operacional. Isso é de propósito — misturar as duas foi o
// que produziu "paguei uma parcela e a tela toda mudou de significado".

/** Sintético: quanto se deve a cada fornecedor no recorte filtrado. */
export const useGerencialFornecedoresPagar = (filtro: FiltroGerencialPagar = {}) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["contas-pagar-gerencial-fornecedores", filtro],
    queryFn: () => contasPagarService.getGerencialFornecedores(filtro),
    placeholderData: (prev) => prev,
  });

  return {
    fornecedores: data?.itens ?? [],
    total: data?.total ?? 0,
    totalPaginas: data?.totalPaginas ?? 0,
    totais: data?.totais ?? TOTAIS_ZERADO,
    isLoading,
    isFetching,
  };
};

/** Analítico: como está cada título (total / pago / saldo). */
export const useGerencialTitulosPagar = (
  filtro: FiltroGerencialPagar = {},
  habilitado = true,
) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["contas-pagar-gerencial-titulos", filtro],
    queryFn: () => contasPagarService.getGerencialTitulos(filtro),
    placeholderData: (prev) => prev,
    // Só busca quando o usuário abriu um fornecedor — evita puxar a base
    // inteira ao entrar na tela.
    enabled: habilitado,
  });

  return {
    titulos: data?.itens ?? [],
    total: data?.total ?? 0,
    totalPaginas: data?.totalPaginas ?? 0,
    totais: data?.totais ?? TOTAIS_ZERADO,
    isLoading,
    isFetching,
  };
};

/** Prazo médio de pagamento (dias entre emissão e pagamento). */
export const usePrazoMedioPagamento = (
  filtro: FiltroGerencialPagar = {},
  habilitado = true,
) => {
  const { data, isLoading } = useQuery({
    queryKey: ["contas-pagar-prazo-medio", filtro],
    queryFn: () => contasPagarService.getPrazoMedio(filtro),
    placeholderData: (prev) => prev,
    enabled: habilitado,
  });

  return {
    prazoMedioDias: data?.prazoMedioDias ?? 0,
    qtdParcelas: data?.qtdParcelas ?? 0,
    valorPago: data?.valorPago ?? 0,
    porFornecedor: data?.porFornecedor ?? [],
    isLoading,
  };
};
